import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/form";
import { buttonVariants } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/data";
import { api } from "@/lib/api";

interface CustomerRow {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  notesSummary: string | null;
}

export function CustomersPage() {
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get<CustomerRow[]>(`/api/v1/customers?q=${encodeURIComponent(q)}`)
      .then(setRows)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [q]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
        <Link to="/customers/new" className={buttonVariants()}>
          Create customer
        </Link>
      </div>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base">Directory</CardTitle>
            <Input
              placeholder="Search name, phone, email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full sm:max-w-xs"
            />
          </div>
        </CardHeader>
        <CardContent>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          <div className="space-y-2 md:hidden">
            {rows.map((c) => (
              <Link key={c.id} to={`/customers/${c.id}`} className="block rounded-xl border p-4 transition hover:border-primary/40 hover:bg-muted/30">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold text-primary">{c.firstName} {c.lastName}</p>
                  <span className="text-xs text-muted-foreground">View</span>
                </div>
                <div className="mt-3 space-y-1 text-sm">
                  <p className="truncate">{c.email ?? "No email"}</p>
                  <p className="text-muted-foreground">{c.phone ?? "No phone"}</p>
                  {c.notesSummary && <p className="border-t pt-2 text-xs text-muted-foreground">{c.notesSummary}</p>}
                </div>
              </Link>
            ))}
            {rows.length === 0 && !loading && <p className="py-6 text-center text-sm text-muted-foreground">No customers yet.</p>}
          </div>
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Link to={`/customers/${c.id}`} className="text-primary hover:underline">
                      {c.firstName} {c.lastName}
                    </Link>
                  </TableCell>
                  <TableCell>{c.email ?? "—"}</TableCell>
                  <TableCell>{c.phone ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{c.notesSummary ?? ""}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">No customers yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
